import { Grid, Theme, Typography } from '@mui/material';
import { DataGridPremium, GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-premium';
import { FC, useCallback, useState } from 'react';

import { OrderableTier, SequencingTier } from '../../../types/leases';
import { EditorModal } from '../../modals';

type ReorderTiersProps = {
	theme: Theme;
	tiers: SequencingTier[];
	handleClose: () => void;
	handleSave: (updatedTiers: OrderableTier[]) => Promise<void>;
};

const columns: GridColDef[] = [
	{
		field: 'rateName',
		headerName: 'Rate',
		type: 'string' as const,
		flex: 1,
		editable: false,
		sortable: false,
		filterable: false,
	},
	{
		field: 'sublotName',
		headerName: 'Sublot',
		type: 'string' as const,
		flex: 1,
		editable: false,
		sortable: false,
		filterable: false,
	},
	{
		field: 'tierNumber',
		headerName: 'Old Tier',
		type: 'number' as const,
		align: 'center' as const,
		headerAlign: 'center' as const,
		flex: 1,
		editable: false,
		sortable: false,
		filterable: false,
	},
	{
		field: 'updatedTierNumber',
		headerName: 'New Tier',
		type: 'number' as const,
		align: 'center' as const,
		headerAlign: 'center' as const,
		flex: 1,
		editable: false,
		sortable: false,
		filterable: false,
	},
];

export const ReorderTiersModal: FC<ReorderTiersProps> = ({ theme, tiers: initialTiers, handleClose, handleSave }) => {
	const tiers: OrderableTier[] = initialTiers.map((tier) => ({
		...tier,
		updatedTierNumber: undefined,
		__reorder__: tier.rateName,
	}));

	const [rows, setRows] = useState<OrderableTier[]>(tiers);

	const handleSubmit = useCallback(() => {
		if (rows.every((row) => !row.updatedTierNumber)) {
			handleClose();
			return;
		}

		if (rows.every((row) => row.tierNumber === row.updatedTierNumber)) {
			handleClose();
			return;
		}

		const updatedTiers = rows.filter((row) => row.tierNumber !== row.updatedTierNumber);
		handleSave(updatedTiers);
	}, [rows, handleClose, handleSave]);

	const handleReorder = (params: GridRowOrderChangeParams) => {
		const rowsCopy = [...rows];
		const row = rowsCopy.splice(params.oldIndex, 1)[0];
		rowsCopy.splice(params.targetIndex, 0, row);

		rowsCopy.forEach((tier, idx) => {
			tier.updatedTierNumber = idx + 1;
		});

		setRows(rowsCopy);
	};

	return (
		<EditorModal
			handleClose={handleClose}
			handleSave={handleSubmit}
			headerText='Reorder Tiers'
			headerBorder
			loading={false}
			theme={theme}
		>
			<Grid
				container
				spacing={2}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignSelf: 'center',
					mt: 2,
					mb: 2,
				}}
			>
				<Grid item xs={12} mb={2}>
					<Typography variant='body1'>Drag and drop tiers into the desired order.</Typography>
				</Grid>
				<Grid item xs={10} mb={2}>
					<DataGridPremium
						autoHeight
						hideFooter
						disableColumnMenu
						rows={rows}
						columns={columns}
						rowReordering
						onRowOrderChange={handleReorder}
					/>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
